import { Bot, Workflow, AudioLines } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Reveal } from "@/components/Reveal";
import { AI_STACK } from "@/config/aiStack";

const ICONS = { chatbots: Bot, automation: Workflow, voice: AudioLines };

/** "AI platforms & frameworks we build with" — tabbed tool chips, Services page. */
export function AiStack() {
  return (
    <section
      id="ai-stack"
      className="py-20 bg-steel text-white blueprint-grid border-y border-slate-800 scroll-mt-28"
      data-testid="ai-stack-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="mb-10 max-w-2xl">
          <span className="font-mono-sub text-xs text-royal-bright uppercase tracking-wider font-bold">
            AI Applications & Capabilities
          </span>
          <h2 className="font-poster text-3xl md:text-4xl font-bold mt-2">
            AI platforms & frameworks we build with
          </h2>
          <p className="text-sm text-slate-300 mt-4 leading-relaxed">
            We pick the right tool for the job — no-code builders when speed matters,
            open-source frameworks when you need control, and managed cloud services
            when you need scale. Everything is deployed and supported like any other
            production system.
          </p>
        </Reveal>

        <Reveal>
          <Tabs defaultValue={AI_STACK[0].key} data-testid="ai-stack-tabs">
            <TabsList className="h-auto flex-wrap justify-start gap-1 bg-steel-light/70 border border-slate-700 p-1">
              {AI_STACK.map((group) => {
                const Icon = ICONS[group.key];
                return (
                  <TabsTrigger
                    key={group.key}
                    value={group.key}
                    data-testid={`ai-stack-tab-${group.key}`}
                    className="gap-2 px-4 py-2 font-mono-sub text-xs uppercase tracking-wider text-slate-300 data-[state=active]:bg-royal data-[state=active]:text-white"
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {group.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {AI_STACK.map((group) => (
              <TabsContent key={group.key} value={group.key} className="mt-6" data-testid={`ai-stack-panel-${group.key}`}>
                <p className="text-sm text-slate-400 mb-5 max-w-2xl">{group.intro}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.tools.map((tool) => (
                    <li
                      key={tool.name}
                      data-testid={`ai-tool-${tool.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group rounded-xl border border-slate-800 bg-steel-light/60 px-4 py-3.5 backdrop-blur transition-colors hover:border-royal/50"
                    >
                      <p className="font-poster font-bold text-sm text-white flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-royal-bright shrink-0" aria-hidden="true" />
                        {tool.name}
                      </p>
                      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{tool.text}</p>
                    </li>
                  ))}
                </ul>
              </TabsContent>
            ))}
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
}
